<template>
  <div
    :id="carouselIdentifier"
    ref="carousel"
    :class="[
      'carousel',
      { 'carousel-vertical': vertical, 'carousel-text-selectable': !swipeOnDesktop },
      { 'carousel--single-page': pageCount <= 0},
      { 'carousel--stack-on-mobile' : stackOnMobile },
    ]"
    @blur="mouseLeave"
    @focus="mouseOver"
    @mouseleave="mouseLeave"
    @mouseover="mouseOver"
  >
    <div class="carousel-wrapper">
      <div
        ref="carouselInner"
        class="carousel-inner"
        :style="{
          flexBasis: `${slideWidth}px`,
          height: height ? `${height}px` : 'auto',
          msFlexPreferredSize: `${slideWidth}px`,
          transform: `translate${vertical ? 'Y' : 'X'}(${currentOffset}px)`,
          transition: transitionStyle,
          visibility: `${slideWidth ? 'visible' : 'hidden'}`}"
      >
        <slot />
      </div>
      <pagination
        v-if="paginationEnabled && pageCount > 0"
        :labels="labels"
      />
    </div>
    <navigation
      ref="carouselNavigation"
      :can-go-forward="canGoForward"
      :can-go-back="canGoBack"
      :show-navigation="showNavigation"
      :autoplay="autoplay"
      :labels="labels"
      @changeSlide="advancePage"
      @toggleAutoPlay="toggleAutoPlay"
    >
      <slot
        slot="customPrevIcon"
        name="customPrevIcon"
      />
      <slot
        slot="customNextIcon"
        name="customNextIcon"
      />
    </navigation>
  </div>
</template>

<script>
  import { AnalyticsHandler } from '@Foundation/analyticsHandler';
  import { eventBus } from '@Foundation/eventbus/eventbus';
  import { debounce } from '@Foundation/utilities/timing';
  import Navigation from '../navigation/navigation.vue';
  import Pagination from '../pagination/pagination.vue';

  export default {
    name: 'Carousel',
    components: {
      Navigation,
      Pagination,
    },

    props: {
      // Remove the carousel functionality on mobile
      stackOnMobile: {
        type: Boolean,
        required: false,
        default: false
      },
      hideArrowsMobile: {
        type: Boolean,
        required: false,
        default: false
      },
      // Copy labels for translation
      labels: {
        type: Object,
        required: false,
        default: () => ({
          navigation: {
            leftArrowAlt: 'Go to the previous slide',
            rightArrowAlt: 'Go to the next slide',
            playAlt: 'play',
            pauseAlt: 'pause'
          },
          pagination: {
            dotAlt: 'Slide {0}',
            dotActiveAlt: 'active',
            dotInactiveAlt: 'inactive'
          }
        })
      },

      // Slide transition easing. any valid CSS transition easing accepted
      easing: { type: String, default: 'ease' },

      // Minimum distance for the swipe to trigger a slide advance
      minSwipeDistance: { type: Number, default: 50 },

      // Flag to render the navigation component (next/prev buttons)
      navigationEnabled: { type: Boolean, default: false },

      // Flag to render pagination component
      paginationEnabled: { type: Boolean, default: true },

      // Maximum number of slides displayed on each page
      perPage: { type: Number, default: 2 },

      // Vertical instead of horizontal
      vertical: { type: Boolean, default: false },

      // the offset between items
      slideSpacing: {
        type: Object,
        default: () => ({})
      },

      // padding for the slides
      slidePadding: { type: Number, default: 0 },

      /**
       * Configure the number of visible slides with a particular browser width.
       * This will be an array of arrays, ex. [[320, 2], [1199, 4]]
       * Formatted as [x, y] where x=browser width, and y=number of slides displayed.
       * ex. [1199, 4] means if (window <= 1199) then show 4 slides per page
       */
      perPageCustom: {
        type: Array,
        default: undefined
      },

      // Scroll per page, not per item
      scrollPerPage: { type: Boolean, default: false },

      // Slide transition speed. Number of milliseconds accepted
      speed: { type: Number, default: 400 },

      // Start at slide
      startAt: { type: Number, default: 0, required: false },

      // Start at slide
      carouselIdentifier: { type: String, default: () => {
        const uuid = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
          const r = Math.random() * 16 | 0, v = c === 'x' ? r : (r & 0x3 | 0x8);
          return v.toString(16);
        });

        return `carousel-${uuid}`;
      } },

      // disable or enable swipe on desktop for Sitecore edit mode
      swipeOnDesktop: { type: Boolean, default: true },

      // disable or enable infinite navigation
      infiniteNavigation: { type: Boolean, default: true },

      // carousel will autoplay until the user interects with pause/play buttons
      autoplay: { type: Boolean, default: false },

      // duration of slides will be updated
      autoplayDuration: { type: Number, default: 3000 },

      // flag to send pagination tagging event
      paginationTagging: { type: Boolean, required: false },
    },

    data() {
      return {
        browserWidth: null,
        carouselSize: null,
        currentPage: this.startAt,
        prevPage: this.startAt,
        dragOffset: 0,
        dragStartX: 0,
        mousedown: false,
        slideCount: 0,
        maxSwipeAngle: 60,
        angle: 0,
        autoplayTimeoutID: null,
        isFocused: false,
        height: null
      };
    },

    computed: {
      canGoForward() {
        return this.infiniteNavigation || this.currentPage < this.pageCount - 1;
      },
      canGoBack() {
        return this.infiniteNavigation || this.currentPage > 0;
      },

      /**
       * Given a viewport width, find the number of slides to display
       * @param  {Number} width - Current viewport width in pixels
       * @return {Number}       Number of slides to display
       */
      breakpointSlidesPerPage() {
        if (!this.perPageCustom) {
          return this.perPage;
        }

        const breakpointArray = this.perPageCustom;
        const width = this.browserWidth;

        const breakpoints = breakpointArray.sort((a, b) => ((a[0] > b[0]) ? -1 : 1));

        // Reduce the breakpoints to entries where the width is in range
        // The breakpoint arrays are formatted as [widthToMatch, numberOfSlides]
        const matches = breakpoints.filter(breakpoint => width >= breakpoint[0]);

        // If there is a match, the result should return only
        // the slide count from the first matching breakpoint
        const match = matches[0] && matches[0][1];

        return match || this.perPage;
      },

      /**
       * Number of slides to display per page in the current context.
       * This is constant unless responsive perPage option is set.
       * @return {Number} The number of slides per page to display
       */
      currentPerPage() {
        return (!this.perPageCustom || this.$isServer)
          ? this.perPage
          : this.breakpointSlidesPerPage;
      },

      /**
       * The horizontal distance the inner wrapper is offset while navigating.
       * @return {Number} Pixel value of offset to apply
       */
      currentOffset() {
        const page = this.currentPage;
        const width = this.slideWidth;
        const dragged = this.dragOffset;

        // The offset distance depends on whether the scrollPerPage option is active.
        // If this option is active, the offset will be determined per page rather than per item.
        const offset = (this.scrollPerPage) ? (page * width * this.currentPerPage) : (page * width);
        const currentOffset = (offset + dragged) * -1;
        const slideSpacing = this.getSlideSpacing();

        return currentOffset + slideSpacing;
      },

      isHidden() {
        return (this.carouselSize <= 0);
      },

      /**
       * Calculate the number of pages of slides
       * @return {Number} Number of pages
       */
      pageCount() {
        const slideCount = this.slideCount; // eslint-disable-line prefer-destructuring
        const perPage = this.currentPerPage;

        if (this.scrollPerPage) {
          const pages = Math.ceil(slideCount / perPage);
          return (pages < 1) ? 1 : pages; // Be sure to not round down to zero pages
        }

        return (slideCount - (this.currentPerPage - 1));
      },

      /**
       * Calculate the width of each slide
       * @return {Number} Slide width
       */
      slideWidth() {
        const width = this.carouselSize;
        const perPage = this.currentPerPage;
        const slideWidth = width / perPage;

        return slideWidth - (this.getSlideSpacing() * 2);
      },

      transitionStyle() {
        return `${this.speed / 1000}s ${this.easing} transform`;
      },

      isMobile() {
        return this.browserWidth < 1024;
      },

      shouldStackOnMobile() {
        return this.stackOnMobile && this.isMobile;
      },

      showNavigation() {
        if (this.isMobile) {
          return this.navigationEnabled && !this.hideArrowsMobile;
        }

        return this.navigationEnabled;
      },

      indicator() {
        return `${this.currentPage + 1} / ${this.pageCount}`;
      }
    },

    beforeUpdate() {
      this.computeCarouselSize();
    },

    mounted() {
      /* istanbul ignore else */
      if (!this.$isServer) {
        window.addEventListener('resize', debounce(this.computeCarouselSize, 16));

        if ('ontouchstart' in window) {
          this.$el.addEventListener('touchstart', this.handleMousedown);
          this.$el.addEventListener('touchend', this.handleMouseup);
          this.$el.addEventListener('touchmove', this.handleMousemove);
        }

        /* istanbul ignore else */
        if (this.swipeOnDesktop) {
          this.$el.addEventListener('mousedown', this.handleMousedown);
          this.$el.addEventListener('mouseup', this.handleMouseup);
          this.$el.addEventListener('mousemove', this.handleMousemove);
        }
      }

      eventBus.$on(`${this.carouselIdentifier}-gotoPage`, (page) => {
        this.goToPage(page);
      });

      this.attachMutationObserver();
      this.computeCarouselSize();

      if (this.autoplay) {
        this.startAutoPlay();
      }
    },

    /* istanbul ignore next */
    beforeDestroy() {
      /* istanbul ignore else */
      if (!this.$isServer) {
        this.detachMutationObserver();
        window.removeEventListener('resize', this.getBrowserWidth);
        if ('ontouchstart' in window) {
          this.$el.removeEventListener('touchmove', this.handleMousemove);
        } else {
          this.$el.removeEventListener('mousemove', this.handleMousemove);
        }
      }
    },

    methods: {
      focusedWithin() {
        return this.isMobile ? false : this.$refs.carousel.contains(document.activeElement);
      },
      mouseOver() {
        if (!this.isMobile) {
          this.isFocused = true;
        }
      },
      mouseLeave() {
        if (!this.isMobile) {
          this.isFocused = false;
        }
      },

      /**
       * Increase/decrease the current page value
       * @param  {String} direction - (Optional) The direction to advance
       */
      advancePage(direction, from = false) {
        if (direction === 'backward') {
          this.goToPage(this.currentPage - 1, from);
        } else {
          this.goToPage(this.currentPage + 1, from);
        }
      },

      /**
       * A mutation observer is used to detect changes to the containing node
       * in order to keep the magnet container in sync with the height its reference node.
       * Note: This does not work for ie10.
       */
      /* istanbul ignore next */
      attachMutationObserver() {
        const MutationObserver = window.MutationObserver
          || window.WebKitMutationObserver
          || window.MozMutationObserver;
        if (MutationObserver) {
          const config = { attributes: true, data: true };
          this.mutationObserver = new MutationObserver(() => {
            this.$nextTick(() => {
              this.computeCarouselSize();
            });
          });
          if (this.$parent.$el) {
            this.mutationObserver.observe(this.$parent.$el, config);
          }
        }
      },

      getSlideSpacing() {
        let value = 0;
        for (const key in this.slideSpacing) {
          if (Object.prototype.hasOwnProperty.call(this.slideSpacing, key) && key <= this.getBrowserWidth()) {
            value = this.slideSpacing[key];
          }
        }
        return value;
      },

      /**
       * Stop listening to mutation changes
       */
      /* instanbul ignore next */
      detachMutationObserver() {
        if (this.mutationObserver) {
          this.mutationObserver.disconnect();
        }
      },

      /**
       * Get the current browser viewport width
       * @return {Number} Browser"s width in pixels
       */
      getBrowserWidth() {
        this.browserWidth = window.innerWidth;
        return this.browserWidth;
      },

      /**
       * Get the width of the carousel DOM element
       * @return {Number} Width of the carousel in pixels
       */
      getCarouselSize() {
        if (this.vertical) {
          this.carouselSize = (this.$el && this.$el.clientHeight) || 0; // Assign globally
        } else {
          this.carouselSize = (this.$el && this.$el.clientWidth) || 0; // Assign globally
        }

        return this.carouselSize;
      },

      /**
       * Filter slot contents to slide instances and return length
       * @return {Number} The number of slides
       */
      getSlideCount() {
        this.slideCount = (
          this.$slots
          && this.$slots.default
          && this.$slots.default.filter(slot =>
            slot.componentOptions
            && slot.componentOptions.tag
            && slot.componentOptions.tag === 'slide').length
        ) || 0;
      },

      /**
       * Set the current page to a specific value
       * This function will only apply the change if the value is within the carousel bounds
       * @param  {Number} page - The value of the new page number
       * @param from
       */
      goToPage(page, from = false) {
        /* istanbul ignore else */
        if (page < 0) {
          if (this.infiniteNavigation) {
            this.currentPage = this.pageCount - 1;
          } else {
            this.currentPage = 0;
          }
        } else if (page >= this.pageCount) {
          if (this.infiniteNavigation) {
            this.currentPage = 0;
          } else {
            this.currentPage = this.pageCount - 1;
          }
        } else {
          this.currentPage = page;
        }
        this.$emit('pageChange', this.currentPage);

        eventBus.$emit(`${this.carouselIdentifier}-pageChange`, this.currentPage, this.prevPage, from);
        this.prevPage = this.currentPage;
      },

      /**
       * Trigger actions when mouse is pressed
       * @param  {Object} e - The event object
       */
      handleMousedown(e) {
        if (!e.touches) {
          e.preventDefault();
        }

        this.mousedown = true;
        this.dragStart = (e.touches) ? e.touches[0] : e;
      },

      /**
       * Trigger actions when mouse is released
       * @param  {Object} e - The event object
       */
      handleMouseup() {
        this.mousedown = false;
        this.dragOffset = 0;
      },

      /**
       * We know the main Distance swiped and the sub direction distance swiped
       * based on that we can calculate the swipe angle
       * @param {number} Opposite
       * @param {number} Adjacent
       * @returns {number} the angle based on Tangent: tan(Î¸) = Opposite / Adjacent
       */
      swipeAngle(main, sub) {
        return Math.atan(Math.abs(sub) / Math.abs(main)) * (180 / Math.PI);
      },

      /**
       * Trigger actions when mouse is pressed and then moved (mouse drag)
       * @param  {Object} e - The event object
       */
      handleMousemove(e) {
        if (!this.mousedown) {
          return;
        }

        const event = (e.touches) ? e.touches[0] : e;

        let mainDirectionDragDistance = 0;
        let subDirectionDragDistance = 0;

        if (this.vertical) {
          mainDirectionDragDistance = (this.dragStart.clientY - event.clientY);
          subDirectionDragDistance = (this.dragStart.clientX - event.clientX);
        } else {
          mainDirectionDragDistance = (this.dragStart.clientX - event.clientX);
          subDirectionDragDistance = (this.dragStart.clientY - event.clientY);
        }

        this.angle = this.swipeAngle(mainDirectionDragDistance, subDirectionDragDistance);

        /**
         * If the swipe angle is less then the max swipe angle then
         * the user is probably using the slideshow to see the next slide
         *
         * We want to use prevent default to prevent the page from scrolling when switching to a new slide.
         */
        if (this.angle < this.maxSwipeAngle) {
          e.preventDefault();
        }

        this.dragOffset = mainDirectionDragDistance;

        if (this.dragOffset > this.minSwipeDistance) {
          this.handleMouseup();
          this.advancePage();
        } else if (this.dragOffset < -this.minSwipeDistance) {
          this.handleMouseup();
          this.advancePage('backward');
        }
      },

      /**
       * Re-compute the width of the carousel and its slides
       */
      /* istanbul ignore next */
      computeCarouselSize() {
        this.getSlideCount();
        this.getBrowserWidth();
        this.getCarouselSize();
        this.stackMobile();
        this.getSlideHeight();
      },

      /**
       * Reset to slide one
       */
      stackMobile() {
        if (this.shouldStackOnMobile) {
          this.goToPage(0);
        }
      },

      /**
       * Recalculate the slide height
       */
      getSlideHeight: debounce(function () {
        this.$nextTick(() => {
          const currentSlide = this.$el.querySelector(`#slide-${this.currentPage}`);
          if (!currentSlide) {
            return;
          }

          const slideHeight = currentSlide.offsetHeight;

          if (this.height !== slideHeight) {
            this.height = slideHeight;
          }
        });
      }, 250),

      /**
       * Starts the autoplay and sets the timeoutID to this.autoplayTimeoutID
       */
      startAutoPlay() {
        this.autoplayTimeoutID = setTimeout(() => {
          if (!this.isFocused && !this.focusedWithin()) {
            this.currentPage = (this.currentPage + 1) % this.pageCount;
          }
          this.startAutoPlay();
        }, this.autoplayDuration);
      },

      /**
       * Clears the autplay timeout and sets the timeoutID back to "null"
       */
      stopAutoPlay() {
        clearTimeout(this.autoplayTimeoutID);
        this.autoplayTimeoutID = null;
      },

      /**
       * If a autoplayTimeoutID is set, clears the timeout.
       * Otherwise, starts the autoplay
       */
      toggleAutoPlay() {
        if (this.autoplayTimeoutID) {
          this.stopAutoPlay();
        } else {
          this.startAutoPlay();
        }
      },

      /**
       * Pushes tagging event to datalayer when the user clicks on the navigation arows
       */
      pushTaggingToDatalayer(direction) {
        if (!this.paginationTagging) {
          return;
        }

        const inner = this.$refs.carouselInner;
        const currentSlide = inner ? inner.children[this.currentPage] : null;
        const currentHeading = currentSlide.querySelector('.hero__content-wrapper');
        const currentSlideTitle = currentSlide && currentHeading ? this.stripHtmlTags(currentHeading.innerHTML) : '';
        const isNavigation = direction && direction === 'pagination';
        const taggingEvent = this.taggingEventObject(isNavigation, direction, currentSlideTitle);

        AnalyticsHandler.pushDataLayer(taggingEvent);
      },
      taggingEventObject(isNavigation, direction, currentSlideTitle) {
        return {
          click_action: isNavigation ? 'display' : this.arrowsTaggingEvent(direction),
          cta_name: currentSlideTitle,
          'event': 'uaevent',
          event_name: 'slider_button_click',
          'ecommerce': 'undefined',
          'eventCategory': 'slider',
          'eventAction': isNavigation ? 'display' : this.arrowsTaggingEvent(direction),
          'eventLabel': `${this.currentPage + 1}::${currentSlideTitle}`
        };
      },
      arrowsTaggingEvent(direction) {
        return (direction === 'backwards') ? (this.labels.navigation ? this.labels.navigation.leftArrowAlt : 'previous') : (this.labels.navigation ? this.labels.navigation.rightArrowAlt : 'next');
      },
      stripHtmlTags(html) {
        const doc = new DOMParser().parseFromString(html, 'text/html');
        return doc.body.textContent.trim() || '';
      }
    }
  };

</script>
<style lang="scss"  src="./carousel.scss"></style>
